import { skipToken } from '@apollo/client/react'
import type { FC, ReactNode } from 'react'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import IcBaselineOpenInNew from '~icons/ic/baseline-open-in-new'
import type { Task } from 'src/services/apollo/generated/graphql'
import { useGetGenerationModelByVersionId } from 'src/services/apollo/generation-model'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { formatQualityTagForDisplay } from 'src/utils/model'
import { getI18nKeyForI2vModelName } from 'src/utils/task'
import type { ParsedParamsForArtworkDetail } from '../ArtworkDetail/ArtworkDetail/parser'
import { parseDisplayableParametersFromTask } from '../ArtworkDetail/ArtworkDetail/parser'
import MediaPreview from '../Common/MediaPreview'
import Duration from '../Duration'
import {
  StyleSeedContentDisplay,
  StyleSeedLabel,
} from '../GenerationTask/StyleSeedDisplay'
import { I2vTaskFrames } from './I2vTaskFrames'

const CollapsedArea: FC<{
  parsedParams: ParsedParamsForArtworkDetail
  summaryExtra?: ReactNode
}> = ({ parsedParams, summaryExtra }) => {
  const { t } = useTranslation(['generation'])

  const items = match(parsedParams)
    .with({ type: 'i2vPro' }, parsedParams => [
      {
        key: 'mode',
        label: t('generation:image-to-video.parameters.mode.label'),
        value: parsedParams.mode,
      },
      {
        key: 'duration',
        label: t('generation:image-to-video.parameters.duration.label'),
        value: parsedParams.duration,
      },
      {
        key: 'camera',
        label: t('generation:image-to-video.parameters.camera.label'),
        value: parsedParams.cameraMovement,
      },
    ])
    .with({ type: 'ref2v' }, parsedParams => [
      {
        key: 'mode',
        label: t('generation:image-to-video.parameters.mode.label'),
        value: parsedParams.mode,
      },
      {
        key: 'duration',
        label: t('generation:image-to-video.parameters.duration.label'),
        value: parsedParams.duration,
      },
    ])
    .with({ type: 'generator' }, parsedParams => {
      return [
        {
          key: 'neg-prompts',
          label: t('generation:negative-prompts.label'),
          value: parsedParams.negativePrompts,
        },
        {
          key: 'sampling-steps',
          label: t('generation:sampling-steps.label'),
          value: parsedParams.samplingSteps,
        },
        {
          key: 'sampling-methods',
          label: t('generation:sampling-method.label'),
          value: parsedParams.samplingMethod,
        },
        {
          key: 'cfgscale',
          label: t('generation:cfg-scale.label'),
          value: parsedParams.cfgScale,
        },
        {
          key: 'seed',
          label: t('generation:seed.label'),
          value: parsedParams.seed,
        },
        {
          key: 'base-image',
          label: t('generation:base-image.label'),
          value: parsedParams.mediaId ? (
            <MediaPreview
              mediaId={parsedParams.mediaId}
              className="w-[200px] h-[200px] bg-no-repeat bg-contain"
            />
          ) : undefined,
        },
        {
          key: 'quality-tag',
          label: t('generation:quality-tag.label'),
          value: parsedParams.qualityTag
            ? formatQualityTagForDisplay(
                parsedParams.qualityTag,
                t(
                  'generation:booster.quality-tag.add-dialog.prompt-placeholder.label',
                ),
              )
            : undefined,
        },
      ]
    })
    .otherwise(() => [])
    .filter(it => !!it.value)

  if (items.length < 1) return null

  return (
    <details>
      <summary className="bg-neutral-100 dark:bg-stone-700 rounded-full px-2 py-1 mt-2 mb-1 w-fit text-sm select-none cursor-pointer">
        {t('generation:detail-params.show.label')}
        {summaryExtra}
      </summary>
      <ItemsTable items={items} />
    </details>
  )
}

const TaskDetailParametersContent: FC<{ task: Task }> = ({ task }) => {
  const { t } = useTranslation(['artwork', 'generation', 'common'])

  const { width, height } = task.outputs ?? {}

  const parsedParams = parseDisplayableParametersFromTask({
    id: task.id,
    outputs: task.outputs,
    parameters: task.parameters,
  })

  /** versionId actually */
  const modelId =
    parsedParams?.type === 'generator' ? parsedParams?.modelId : undefined

  let overrideModelName: string | undefined
  if (
    parsedParams?.type === 'i2vPro' ||
    parsedParams?.type === 'ref2v' ||
    parsedParams?.type === 't2i2v'
  ) {
    overrideModelName = getI18nKeyForI2vModelName(parsedParams.model)
  }

  const { data: { model: customModel } = {}, loading: loadingModel } =
    useGetGenerationModelByVersionId(modelId || skipToken)

  const modelUsed = useMemo(() => {
    return loadingModel ? (
      '...'
    ) : customModel?.title ? (
      <span className="inline-flex gap-2 items-center">
        {customModel.title}
        <Link to={`/model/${customModel.id}`} target="_blank">
          <IcBaselineOpenInNew />
        </Link>
      </span>
    ) : (
      t([
        `generation:image-to-video.parameters.model.${overrideModelName}.label`,
        'common:unknown',
      ])
    )
  }, [loadingModel, customModel, overrideModelName])

  if (!parsedParams?.type || !task) return null

  const { id, createdAt, startedAt, endAt, status } = task

  const styleMixSeed =
    parsedParams.type === 'generator' &&
    parsedParams.styleMix?.enabled &&
    Number(parsedParams.styleMix.seed)
      ? parsedParams.styleMix.seed
      : undefined
  const outsideItems = [
    { key: 'ID', label: 'ID', value: <pre key="id">{id}</pre> },
    {
      key: 'created-at',
      label: t('artwork:generation.submit-time'),
      value: dayjs(createdAt).format('lll'),
    },
    {
      key: 'started-at',
      label: t('artwork:generation.start-time'),
      value: startedAt ? dayjs(startedAt).format('lll') : 'not started',
    },
    {
      key: 'waiting-time',
      label: t('artwork:generation.waiting-time'),
      value: (
        <>
          <div className="min-w-[100px]">
            {status === 'cancelled' ? (
              '(N/A)'
            ) : (
              <Duration startTime={createdAt} endTime={startedAt} />
            )}
          </div>
        </>
      ),
    },
    {
      key: 'elapsed',
      label: t('artwork:generation.generating-time'),
      value: startedAt ? (
        <Duration startTime={startedAt} endTime={endAt} />
      ) : (
        'not started'
      ),
    },
    {
      key: 'size',
      label: t('artwork:generation.size'),
      value:
        width && height ? (
          <>
            {width}x{height}
          </>
        ) : null,
    },
    {
      key: 'model',
      label: t('artwork:generation.model-used'),
      value: modelUsed,
    },
    {
      key: 'style-seed',
      label: <StyleSeedLabel key="style-seed" />,
      value: styleMixSeed ? (
        <StyleSeedContentDisplay seed={styleMixSeed} />
      ) : undefined,
    },
  ]

  return (
    <>
      <ItemsTable items={outsideItems} omitEmptyValue />
      <CollapsedArea parsedParams={parsedParams} />
      {parsedParams?.type === 'i2vPro' ? (
        <I2vTaskFrames
          mediaId={parsedParams.mediaId}
          tailMediaId={parsedParams.tailMediaId}
          className="text-sm font-medium text-neutral-500 dark:text-neutral-400"
        />
      ) : null}
    </>
  )
}

function ItemsTable(
  props: {
    items: Array<{ label: ReactNode; value: ReactNode; key: string }>
    omitEmptyValue?: boolean
  } & StyleProps,
) {
  const items = props.omitEmptyValue
    ? props.items.filter(it => !!it.value)
    : props.items
  return (
    <table className={cx('table-auto text-sm', props.className)}>
      <tbody>
        {items.map(it => (
          <tr key={it.key}>
            <td className="pr-4 whitespace-nowrap font-medium align-top text-neutral-500 dark:text-neutral-400">
              {it.label}
            </td>
            <td className="w-full max-msm:text-right">{it.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export const TaskDetailParameters = memo(TaskDetailParametersContent)
